import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BarChart3, Loader2, Menu, Plus, Tag, Trash2 } from 'lucide-react';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import PromotionWizardModal from '@/components/promotions/PromotionWizardModal';
import PromotionScopeSummary from '@/components/promotions/PromotionScopeSummary';
import { useUIStore } from '@/store/uiStore';
import { promotionsApi } from '@/services/api';
import { campaignStatus, describeOffer, type PromotionCampaign, type PromotionStatus } from '@/utils/promotions';

const STATUS_CLASS: Record<PromotionStatus, string> = {
  Active: 'bg-emerald-50 text-emerald-800',
  Scheduled: 'bg-blue-50 text-blue-800',
  Ended: 'bg-gray-100 text-gray-600',
  Paused: 'bg-amber-50 text-amber-800',
};

function formatWhen(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function PromotionsPage() {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PromotionCampaign | null>(null);

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['promotionCampaigns'],
    queryFn: async () => {
      const res = await promotionsApi.list();
      return res.data.message as PromotionCampaign[];
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ name, is_active }: { name: string; is_active: number }) =>
      promotionsApi.setActive(name, is_active),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['promotionCampaigns'] });
      void qc.invalidateQueries({ queryKey: ['posPromotions'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => promotionsApi.delete(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['promotionCampaigns'] });
      void qc.invalidateQueries({ queryKey: ['posPromotions'] });
    },
  });

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <TrialBanner />
        <div className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSidebar}
                className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Tag className="w-7 h-7 text-primary-600" />
                  {t('promotions.title')}
                </h1>
                <p className="text-gray-500 text-sm">{t('promotions.subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/reports/sales/promotions"
                className="btn-secondary inline-flex items-center gap-2 text-sm"
              >
                <BarChart3 className="w-4 h-4" />
                {t('promotions.viewReport')}
              </Link>
              <button
                type="button"
                className="btn-primary inline-flex items-center gap-2"
                onClick={() => {
                  setEditing(null);
                  setModalOpen(true);
                }}
              >
                <Plus className="w-4 h-4" />
                {t('promotions.newSale')}
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900 mb-6">
            {t('promotions.safeBanner')}
          </div>

          {isLoading ? (
            <div className="flex justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="card p-10 text-center">
              <Tag className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-gray-900">{t('promotions.emptyTitle')}</h2>
              <p className="text-sm text-gray-500 mt-1 mb-4">{t('promotions.emptyHelp')}</p>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  setEditing(null);
                  setModalOpen(true);
                }}
              >
                {t('promotions.newSale')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {campaigns.map((c) => {
                const status = c.status || campaignStatus(c);
                const on = !!c.is_active;
                return (
                  <div key={c.name} className="card p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900 truncate">{c.campaign_name}</h3>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_CLASS[status]}`}>
                            {t(`promotions.status.${status.toLowerCase()}`)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {formatWhen(c.valid_from)} → {formatWhen(c.valid_to)}
                        </p>
                        {(c.rules || []).length ? (
                          <div className="mt-3 space-y-3">
                            {(c.rules || []).map((rule, idx) => (
                              <div
                                key={rule.name || idx}
                                className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2.5 space-y-2"
                              >
                                <p className="text-sm font-medium text-gray-900">{describeOffer(rule)}</p>
                                <PromotionScopeSummary rule={rule} />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-700 mt-2">{t('promotions.noOffers')}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          {t(`promotions.who.${(c.customer_types || 'Retailer').toLowerCase()}`)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => c.name && toggleMutation.mutate({ name: c.name, is_active: on ? 0 : 1 })}
                        className={`shrink-0 relative w-12 h-7 rounded-full transition-colors ${
                          on ? 'bg-emerald-500' : 'bg-gray-300'
                        }`}
                        title={on ? t('promotions.turnOff') : t('promotions.turnOn')}
                      >
                        <span
                          className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${
                            on ? 'left-5' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <button
                        type="button"
                        className="btn-secondary text-sm"
                        onClick={() => {
                          setEditing(c);
                          setModalOpen(true);
                        }}
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        type="button"
                        className="text-sm text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
                        onClick={() => {
                          if (c.name && window.confirm(t('promotions.deleteConfirm'))) {
                            deleteMutation.mutate(c.name);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <PromotionWizardModal
        isOpen={modalOpen}
        editing={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
}
